import {
    Combobox,
    InputBase,
    Pill,
    PillsInput,
    useCombobox,
} from '@mantine/core';
import { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { useDebouncedCallback } from '@mantine/hooks';
import { usePaginatedQuery } from '../../../http/queries/usePaginatedQuery';

const groceries = [
    '🍎 Apples',
    '🍌 Bananas',
    '🥦 Broccoli',
    '🥕 Carrots',
    '🍫 Chocolate',
    '🍇 Grapes',
];

function SearchableSelectCreatable({
    label,
    placeholder,
    onChange,
    queryHttpFn,
    queryKey,
    optionsMapFn,
    ...props
}) {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => queryHttpFn(queryParams),
        queryKey: [queryKey],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting: [],
    });

    const { data: queryData, pagination: queryPagination } =
        paginatedData || {};

    const selectOptions = useMemo(() => {
        if (!queryData) {
            return [];
        }

        if (!optionsMapFn) {
            console.warn('no optionsMapFn');
            return [];
        }
        return queryData.map(optionsMapFn);
    }, [queryData, isLoading]);

    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [data, setData] = useState([]);
    const [value, setValue] = useState('');
    const [search, setSearch] = useState('');

    const exactOptionMatch = data.some((item) => item === search);
    const filteredOptions = exactOptionMatch
        ? data
        : [...data, ...selectOptions].filter(
              (item) =>
                  !search ||
                  item?.toLowerCase()?.includes(search.toLowerCase().trim())
          );

    const options = filteredOptions.map((item) => (
        <Combobox.Option
            value={item}
            key={item}>
            {item}
        </Combobox.Option>
    ));

    const handleValueSelect = (val) => {
        setSearch('');
        if (val === '$create') {
            try {
                if (search == null) {
                    return;
                }

                const existing = data.find(
                    (x) => x?.toLowerCase() === search?.toLowerCase()
                );
                if (existing) {
                    setValue(search);
                    setSearch('');
                    if (onChange) {
                        onChange(search);
                    }
                } else {
                    setData((current) => [...current, search]);
                    setValue(search);

                    if (onChange) {
                        onChange(search);
                    }
                }
            } finally {
                combobox.closeDropdown();
            }
            return;
        }

        setValue(val);
        if (onChange) {
            onChange(val);
        }
        combobox.closeDropdown();
    };

    const handleValueRemove = (val) => {
        setValue(null);
    };

    return (
        <Combobox
            store={combobox}
            label={label}
            onOptionSubmit={handleValueSelect}
            rightSection={
                isLoading ? (
                    <FontAwesomeIcon
                        icon={faSpinnerThird}
                        spin
                    />
                ) : (
                    <Combobox.Chevron onClick={combobox.openDropdown} />
                )
            }>
            <Combobox.Target>
                <PillsInput
                    rightSection={<Combobox.Chevron />}
                    {...props}>
                    <Pill.Group>
                        {value ? (
                            <Pill
                                withRemoveButton
                                onRemove={() => handleValueRemove()}>
                                {value}
                            </Pill>
                        ) : null}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                value={search}
                                placeholder={
                                    !value ? placeholder || 'Search values' : ''
                                }
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                    filterDebounce(event.currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Backspace' &&
                                        search.length === 0
                                    ) {
                                        event.preventDefault();
                                        handleValueRemove();
                                    } else if (event.key === 'Enter') {
                                        handleValueSelect('$create');
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {options}
                    {!exactOptionMatch && search.length > 0 && (
                        <Combobox.Option value='$create'>
                            + Create {search}
                        </Combobox.Option>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
}
export default SearchableSelectCreatable;
